/**
 * QuickModePanel.js - Quick Mode UI for Video Creation
 * AI automatically decides all settings - one-click video generation
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  FiUpload,
  FiImage,
  FiX,
  FiZap,
  FiCheck,
  FiMic,
  FiVideo,
  FiAlertCircle,
  FiSliders,
  FiInfo,
  FiChevronDown,
} from "react-icons/fi";
import { sanitizeError } from "../utils/errorSanitizer";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AISettingsPreview from "./AISettingsPreview";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3010";

// Progress steps
const PROGRESS_STEPS = [
  { id: "analyzing", icon: "✨", title: "Phân tích sản phẩm", message: "Đang phân tích hình ảnh và tạo kịch bản..." },
  { id: "generating_audio", icon: "🎤", title: "Tạo giọng đọc", message: "Đang tạo giọng đọc AI..." },
  { id: "complete", icon: "🎬", title: "Hoàn thành", message: "Video đã sẵn sàng!" },
];

// Unique ID counter for stable drag keys
let qmImageIdCounter = 0;
const generateQmImageId = () => `qm_${Date.now()}_${++qmImageIdCounter}`;

// Sortable image item for Quick Mode
const QmSortableImageItem = ({ id, src, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    transition: {
      duration: 200,
      easing: "cubic-bezier(0.25, 1, 0.5, 1)",
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: "none",
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`image-preview-item ${isDragging ? "dragging" : ""}`}
    >
      <div {...attributes} {...listeners} className="image-drag-handle" />
      <img src={src} alt={`Preview ${index + 1}`} />
      <div className="image-order-badge">{index + 1}</div>
      <button
        className="image-preview-remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
      >
        <FiX />
      </button>
    </div>
  );
};

const QuickModePanel = ({ onComplete, existingImages = [] }) => {
  const [imageItems, setImageItems] = useState(() =>
    existingImages.map((src) => ({ id: generateQmImageId(), src }))
  );
  const images = imageItems.map((item) => item.src);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);
  const fileInputRef = useRef(null);
  const unsubRef = useRef(null); // RTDB listener cleanup ref

  const userId = localStorage.getItem("userId");

  // Cleanup RTDB listener on unmount
  useEffect(() => {
    return () => {
      if (unsubRef.current?.off) {
        unsubRef.current.off();
        unsubRef.current = null;
      }
    };
  }, []);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 8 },
    }),
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setImageItems((prev) => {
        const oldIndex = prev.findIndex((item) => item.id === active.id);
        const newIndex = prev.findIndex((item) => item.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  }, []);

  // Handle file upload
  const handleFileSelect = useCallback((files) => {
    const validFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/")
    );

    if (validFiles.length + imageItems.length > 10) {
      setError("Tối đa 10 hình ảnh");
      return;
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageItems((prev) => [...prev, { id: generateQmImageId(), src: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });

    setError(null);
  }, [imageItems.length]);

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Remove image by stable ID
  const removeImage = (id) => {
    setImageItems((prev) => prev.filter((item) => item.id !== id));
  };

  // Generate video (async job pattern)
  const handleGenerate = async () => {
    if (images.length === 0) {
      setError("Vui lòng thêm ít nhất 1 hình ảnh");
      return;
    }

    if (!userId) {
      setError("Vui lòng đăng nhập để sử dụng tính năng này");
      return;
    }

    setIsProcessing(true);
    setError(null);
    setCurrentStep("analyzing");

    try {
      // Step 1: Create async job — server responds immediately with jobId
      const response = await fetch(`${API_BASE_URL}/api/video/quick-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          images: images,
          userId: userId,
          additionalInfo: additionalInfo.trim() || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 402) {
          throw new Error("Không đủ điểm. Vui lòng nạp thêm điểm để tiếp tục.");
        }
        throw new Error(data.error || "Có lỗi xảy ra");
      }

      const { jobId } = data;

      if (!jobId) {
        throw new Error("Không nhận được mã công việc từ server");
      }

      // Step 2: Listen to Firebase RTDB for real-time progress updates
      const { ref, onValue, off } = await import("firebase/database");
      const { db: firebaseDb } = await import("../firebase/config");

      const jobRef = ref(firebaseDb, `jobs/${jobId}`);

      const unsubscribe = onValue(jobRef, (snapshot) => {
        const job = snapshot.val();
        if (!job) return;

        // Map progress to UI steps
        if (job.progress < 50) {
          setCurrentStep("analyzing");
        } else if (job.progress < 90) {
          setCurrentStep("generating_audio");
        }

        // Job completed
        if (job.status === "completed" && job.result) {
          setCurrentStep("complete");
          setResult(job.result);
          setIsProcessing(false);

          // Cleanup listener
          off(jobRef);

          // Notify parent
          setTimeout(() => {
            if (onComplete) {
              onComplete(job.result, images);
            }
          }, 1500);
        }

        // Job failed
        if (job.status === "failed") {
          setError(sanitizeError(job.error || "Có lỗi xảy ra khi tạo video. Vui lòng thử lại."));
          setCurrentStep(null);
          setIsProcessing(false);
          off(jobRef);
        }
      });

      // Store unsubscribe for cleanup on unmount
      unsubRef.current = { jobRef, off: () => off(jobRef) };

    } catch (err) {
      console.error("Quick generate error:", err);
      setError(sanitizeError(err));
      setCurrentStep(null);
      setIsProcessing(false);
    }
  };

  // Create new video
  const handleReset = () => {
    // Cleanup RTDB listener if active
    if (unsubRef.current?.off) {
      unsubRef.current.off();
      unsubRef.current = null;
    }
    setImageItems([]);
    setResult(null);
    setCurrentStep(null);
    setError(null);
    setIsProcessing(false);
  };

  return (
    <div className="quick-mode-panel">
      {/* Description chips */}
      <div className="quick-mode-description">
        <span>✅ AI tạo kịch bản</span>
        <span>✅ AI chọn giọng đọc</span>
        <span>✅ AI chọn hiệu ứng</span>
        <span>✅ Tự động phụ đề</span>
      </div>

      {/* Error message */}
      {error && (
        <div className="error-message">
          <FiAlertCircle />
          <span>{error}</span>
        </div>
      )}

      {/* Result state */}
      {result ? (
        <>
          <AISettingsPreview settings={result.settings} script={result.script} />
          <div className="export-buttons" style={{ marginTop: "1.5rem" }}>
            <button className="export-button secondary" onClick={handleReset}>
              <FiZap />
              Tạo video mới
            </button>
            <button
              className="export-button primary"
              onClick={() => onComplete && onComplete(result, images)}
            >
              <FiSliders />
              Chỉnh sửa & Xuất video
            </button>
          </div>
        </>
      ) : (
        <>
          {/* Image uploader */}
          {images.length === 0 ? (
            <div
              className={`image-uploader ${isDragOver ? "drag-over" : ""}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="image-uploader-icon">📸</div>
              <div className="image-uploader-text">
                Kéo thả hoặc click để tải ảnh lên
              </div>
              <div className="image-uploader-hint">
                Tối đa 10 ảnh • JPG, PNG, WebP
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => handleFileSelect(e.target.files)}
              />
            </div>
          ) : (
            <>
              {/* Image preview grid with drag-and-drop */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={imageItems.map((item) => item.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="image-preview-grid">
                    {imageItems.map((item, index) => (
                      <QmSortableImageItem
                        key={item.id}
                        id={item.id}
                        src={item.src}
                        index={index}
                        onRemove={removeImage}
                      />
                    ))}
                    {imageItems.length < 10 && (
                      <div
                        className="image-uploader"
                        style={{ aspectRatio: "1", padding: "1rem" }}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <FiUpload style={{ fontSize: "1.5rem", color: "#9ca3af" }} />
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          style={{ display: "none" }}
                          onChange={(e) => handleFileSelect(e.target.files)}
                        />
                      </div>
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}

          {/* Additional info toggle */}
          <div className="additional-info-section quick-mode">
            <button
              className="additional-info-toggle"
              onClick={() => setShowAdditionalInfo(!showAdditionalInfo)}
              type="button"
            >
              <FiInfo />
              Thông tin bổ sung (tùy chọn)
              <FiChevronDown
                style={{
                  transform: showAdditionalInfo ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s",
                  marginLeft: "auto",
                }}
              />
            </button>
            {showAdditionalInfo && (
              <>
                <textarea
                  className="additional-info-textarea"
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  placeholder={"Ví dụ:\n• Tên sản phẩm: Kem chống nắng ABC SPF50\n• Giá: 299.000đ (giảm 30%)\n• Đặc điểm nổi bật, đối tượng khách hàng..."}
                  maxLength={1000}
                />
                <div className="additional-info-hint">
                  Giúp AI tạo video chính xác hơn
                  {additionalInfo.length > 0 && (
                    <span> • {additionalInfo.length}/1000</span>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Generate button */}
          <button
            className={`generate-button ${isProcessing ? 'loading' : ''}`}
            onClick={handleGenerate}
            disabled={images.length === 0 || isProcessing}
          >
            {isProcessing ? (
              <>
                <span className="spinner"></span>
                <span>Đang xử lý...</span>
              </>
            ) : (
              <>
                <FiZap />
                <span>Tạo video tự động</span>
              </>
            )}
          </button>

          {/* Processing progress */}
          {isProcessing && currentStep && (
            <div className="processing-progress">
              {PROGRESS_STEPS.map((step) => {
                const stepIndex = PROGRESS_STEPS.findIndex((s) => s.id === step.id);
                const currentIndex = PROGRESS_STEPS.findIndex(
                  (s) => s.id === currentStep
                );
                const isCompleted = stepIndex < currentIndex;
                const isActive = step.id === currentStep;

                return (
                  <div
                    key={step.id}
                    className={`progress-step ${
                      isActive ? "active" : isCompleted ? "completed" : "pending"
                    }`}
                  >
                    <div className="progress-step-icon">
                      {isCompleted ? <FiCheck /> : step.icon}
                    </div>
                    <div className="progress-step-text">
                      <div className="progress-step-title">{step.title}</div>
                      <div className="progress-step-message">{step.message}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuickModePanel;
